import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import TeamMemberForm from './TeamMemberForm';

const BASE_URL = '/admin/team';

const toSnake = m => ({
  id:              m.id,
  active:          m.active,
  first_name:      m.firstName,
  last_name:       m.lastName,
  role:            m.role,
  email:           m.email,
  slack_handle:    m.slackHandle,
  jira_name:       m.jiraName,
  jira_account_id: m.jiraAccountId,
  start_date:      m.startDate,
});

const toCamel = data => ({
  active:          data.active,
  firstName:       data.first_name,
  lastName:        data.last_name,
  role:            data.role,
  email:           data.email,
  slackHandle:     data.slack_handle,
  jiraName:        data.jira_name,
  jiraAccountId:   data.jira_account_id,
  startDate:       data.start_date,
});

function AdminTeamMembers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  const editing = id === undefined ? null
    : members.find(m => String(m.id) === id) ?? null;

  // Bad/stale id (e.g. deleted item, typo'd link) -> back to the list,
  // but only once loading has actually finished.
  useEffect(() => {
    if (!loading && id && !editing) navigate(BASE_URL, { replace: true });
  }, [loading, id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    setLoading(true);
    api.get(`${QUALITY_SERVICE_API_URL}/hub/quality-team`)
      .then(r => setMembers(r.data.map(toSnake)))
      .catch(() => setError('Failed to load team members.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async (data) => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`${QUALITY_SERVICE_API_URL}/hub/quality-team/${editing.id}`, toCamel(data));
      navigate(BASE_URL);
      load();
    } catch {
      setError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const active   = members.filter(m => m.active);
  const inactive = members.filter(m => !m.active);

  return (
    <div className="admin-campaigns">
      <div className="admin-section-toolbar">
        <h2 className="admin-section-title">Quality Team</h2>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <MemberTable members={active} onEdit={m => navigate(`${BASE_URL}/${m.id}`)} />

          {inactive.length > 0 && (
            <>
              <h3 className="admin-subsection-title">Former Members</h3>
              <MemberTable members={inactive} onEdit={m => navigate(`${BASE_URL}/${m.id}`)} />
            </>
          )}
        </>
      )}

      {editing !== null && (
        <TeamMemberForm
          member={editing}
          onSave={handleSave}
          onCancel={() => { navigate(BASE_URL); setError(null); }}
          saving={saving}
        />
      )}
    </div>
  );
}

function MemberTable({ members, onEdit }) {
  return (
    <table className="admin-table" style={{ marginBottom: '24px' }}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Role</th>
          <th>Email</th>
          <th>Slack</th>
          <th>Jira Name</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {members.map(m => (
          <tr key={m.id}>
            <td className="admin-td-name">{m.first_name} {m.last_name}</td>
            <td>{m.role || '—'}</td>
            <td>{m.email || '—'}</td>
            <td>{m.slack_handle || '—'}</td>
            <td>{m.jira_name || '—'}</td>
            <td className="admin-td-actions">
              <button className="admin-btn-edit" onClick={() => onEdit(m)}>Edit</button>
            </td>
          </tr>
        ))}
        {members.length === 0 && (
          <tr><td colSpan={6} className="admin-empty">No members.</td></tr>
        )}
      </tbody>
    </table>
  );
}

export default AdminTeamMembers;
